export const BridgeProvider = {
  SYMBIOSIS: 'symbiosis',
  RANGO: 'rango',
  LAYERSWAP: 'layerswap',
} as const
export type BridgeProvider = (typeof BridgeProvider)[keyof typeof BridgeProvider]

export const SwapProvider = {
  STON: 'ston',
  JUPITER: 'jupiter',
} as const
export type SwapProvider = (typeof SwapProvider)[keyof typeof SwapProvider]

export const ChainId = {
  ETHEREUM: 1,
  ARBITRUM: 42161,
  BASE: 8453,
  OPTIMISM: 10,
  SOLANA: 5426,
} as const
export type ChainId = (typeof ChainId)[keyof typeof ChainId]

export const FlowStatus = {
  IDLE: 'idle',
  WALLET_GATE: 'wallet_gate',
  QUOTE_LOADING: 'quote_loading',
  QUOTE_READY: 'quote_ready',
  BRIDGE_APPROVING: 'bridge_approving',
  BRIDGE_SWAPPING: 'bridge_swapping',
  BRIDGE_TRACKING: 'bridge_tracking',
  SOLANA_JUPITER_SWAPPING: 'solana_jupiter_swapping',
  SOLANA_LAYERSWAP_BRIDGING: 'solana_layerswap_bridging',
  INTERMEDIATE_SYMBIOSIS_APPROVING: 'intermediate_symbiosis_approving',
  INTERMEDIATE_SYMBIOSIS_SWAPPING: 'intermediate_symbiosis_swapping',
  INTERMEDIATE_SYMBIOSIS_TRACKING: 'intermediate_symbiosis_tracking',
  DESTINATION_SWAP_READY: 'destination_swap_ready',
  DESTINATION_SWAPPING: 'destination_swapping',
  SUCCESS: 'success',
  ERROR: 'error',
} as const
export type FlowStatus = (typeof FlowStatus)[keyof typeof FlowStatus]

export const ErrorClass = {
  WALLET_NOT_CONNECTED: 'wallet_not_connected',
  WALLET_SESSION_DEAD: 'wallet_session_dead',
  WALLET_NAMESPACE_MISMATCH: 'wallet_namespace_mismatch',
  UNSUPPORTED_CHAIN: 'unsupported_chain',
  ROUTE_NOT_FOUND: 'route_not_found',
  APPROVAL_REJECTED: 'approval_rejected',
  SWAP_REJECTED: 'swap_rejected',
  SWAP_TIMEOUT: 'swap_timeout',
  BRIDGE_FAILED: 'bridge_failed',
  TON_TX_FAILED: 'ton_tx_failed',
  TON_TX_UNCERTAIN: 'ton_tx_uncertain',
  TON_SYNC_REQUIRED: 'ton_sync_required',
  SOLANA_TX_FAILED: 'solana_tx_failed',
  SOLANA_TX_UNCERTAIN: 'solana_tx_uncertain',
  AMOUNT_TOO_SMALL: 'amount_too_small',
  AMOUNT_TOO_LARGE: 'amount_too_large',
  SOLANA_SWAP_FAILED: 'solana_swap_failed',
  INSUFFICIENT_SOL_FEES: 'insufficient_sol_fees',
  WRONG_CHAIN: 'wrong_chain',
  QUOTE_TIMEOUT: 'quote_timeout',
  BRIDGE_CREATE_FAILED: 'bridge_create_failed',
  ROUTE_REVERTED: 'route_reverted',
} as const
export type ErrorClass = (typeof ErrorClass)[keyof typeof ErrorClass]

export const TonLegSubStatus = {
  USDT_TO_TON: 'usdt_to_ton',
  SETTLING: 'settling',
  TON_TO_SPOTTY: 'ton_to_spotty',
  TON_TO_SPOTTY_PENDING: 'ton_to_spotty_pending',
} as const
export type TonLegSubStatus = (typeof TonLegSubStatus)[keyof typeof TonLegSubStatus]

export const ACTIVE_BRIDGE_STATUSES: ReadonlySet<string> = new Set([
  FlowStatus.BRIDGE_APPROVING,
  FlowStatus.BRIDGE_SWAPPING,
  FlowStatus.BRIDGE_TRACKING,
  FlowStatus.DESTINATION_SWAPPING,
  FlowStatus.SOLANA_JUPITER_SWAPPING,
  FlowStatus.SOLANA_LAYERSWAP_BRIDGING,
  FlowStatus.INTERMEDIATE_SYMBIOSIS_APPROVING,
  FlowStatus.INTERMEDIATE_SYMBIOSIS_SWAPPING,
  FlowStatus.INTERMEDIATE_SYMBIOSIS_TRACKING,
])

const RECOVERABLE_ERRORS: ReadonlySet<ErrorClass> = new Set([
  ErrorClass.SWAP_REJECTED,
  ErrorClass.APPROVAL_REJECTED,
  ErrorClass.WRONG_CHAIN,
  ErrorClass.TON_TX_FAILED,
  ErrorClass.SWAP_TIMEOUT,
  ErrorClass.SOLANA_SWAP_FAILED,
  ErrorClass.SOLANA_TX_FAILED,
  ErrorClass.BRIDGE_FAILED,
  ErrorClass.BRIDGE_CREATE_FAILED,
  ErrorClass.AMOUNT_TOO_SMALL,
  ErrorClass.AMOUNT_TOO_LARGE,
  ErrorClass.ROUTE_NOT_FOUND,
  ErrorClass.INSUFFICIENT_SOL_FEES,
  ErrorClass.QUOTE_TIMEOUT,
  ErrorClass.ROUTE_REVERTED,
])

export const isRecoverableError = (error: ErrorClass | null): boolean =>
  error !== null && RECOVERABLE_ERRORS.has(error)
